import { Box, Button, HStack, Heading, Image, Text, VStack, ButtonProps, Stack } from '@chakra-ui/react'
import { PaywalledAction } from '@src/components/sco/PaywalledAction'
import { ChevronRightIcon } from '@src/components/sco/icons'
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider'
import { NextLink, NextLinkProps } from '@src/components/sco/utilities/NextLink'
import { preventOrphanWords } from '@src/lib/textUtils'
import NextImage from 'next/image'
import React, { useCallback } from 'react'

interface LPHeroProps {
  buttons?: Array<ButtonProps & NextLinkProps>
  buttonLink?: string
  buttonText?: string
  buttonSize?: React.ReactNode
  children?: React.ReactNode
  contentPermission?: string
  description?: string
  longDescription?: string
  featuredImage?: string
  imageAltText?: string
  leftIcon?: React.ReactElement
  paywalledAction?: boolean
  paywalledActionLink?: string
  secondaryButtonText?: string
  secondaryButtonLink?: string
  showButtonBottom?: boolean
  showButtonTop?: boolean
  title: string
  headingSize?: React.ReactNode
  descriptionSize?: React.ReactNode
  variant?: any
}

const LPHero = (props: LPHeroProps) => {
  const { getBreakpointValue } = useBreakpointContext()

  const renderButtons = useCallback(() => {
    if (props.paywalledAction) {
      return (
        <HStack gap={getBreakpointValue(['1em', '1em', '2em'])}>
          <PaywalledAction
            contentPermission={props.contentPermission || 'canAccessFGDashboard'}
            variant={'button-only'}
          >
            <Button asChild size={getBreakpointValue(['md', 'lg'])} variant={'cta'}>
              <NextLink href={props.paywalledActionLink}>
                {props.buttonText}
                <ChevronRightIcon />
              </NextLink>
            </Button>
          </PaywalledAction>
        </HStack>
      )
    }

    return (
      <Stack
        align={'stretch'}
        direction={['column', 'column', 'row']}
        gap={getBreakpointValue(['1em', '1em', '1.5em'])}
        justify={'center'}
        paddingX={getBreakpointValue([4, 4, 4, 8])}
        width={'full'}
      >
        {props.buttons &&
          props.buttons.map((button, idx) => (
            <Button {...button} asChild>
              <NextLink key={idx}>{button.title}</NextLink>
            </Button>
          ))}
        {props.secondaryButtonText && (
          <Button asChild size={props.buttonSize || getBreakpointValue(['md', 'lg'])}>
            <NextLink href={props.secondaryButtonLink}>{props.secondaryButtonText}</NextLink>
          </Button>
        )}
        {props.buttonText && (
          <Button
            asChild
            marginTop={getBreakpointValue([0, 0, 0, 4])}
            size={props.buttonSize || getBreakpointValue(['md', 'lg'])}
            variant={'cta'}
          >
            <NextLink href={props.buttonLink}>
              {props.buttonText}
              <ChevronRightIcon />
            </NextLink>
          </Button>
        )}
      </Stack>
    )
  }, [props])

  return (
    <VStack
      align={'center'}
      gap={getBreakpointValue([0, 0, 4, 8])}
      justify={'center'}
      maxWidth={'80em'}
      width={'full'}
    >
      <VStack gap={getBreakpointValue([0, 0, 5, 8])} width={'full'}>
        <VStack align={'center'} gap={2} justify={'center'}>
          <Heading
            as={'h1'}
            background={'linear-gradient(90deg, #d97258 0%, #9858a3 100%)'}
            backgroundClip={'text'}
            color={'transparent'}
            size={props.headingSize || getBreakpointValue(['xl', '4xl', '4xl', '5xl'])}
            textAlign={'center'}
          >
            {props.title}
          </Heading>
          {props.description && (
            <Text
              as={'h2'}
              color={'mochaLight'}
              fontSize={props.descriptionSize || getBreakpointValue(['lg', 'xl', '2xl', '2xl'])}
              fontWeight={600}
              textAlign={'center'}
              dangerouslySetInnerHTML={{
                __html: preventOrphanWords(props.description),
              }}
            />
          )}
          {props.longDescription && (
            <Box margin={4} width={'container.md'}>
              <Text
                fontSize={'2xl'}
                textAlign={'center'}
                dangerouslySetInnerHTML={{
                  __html: preventOrphanWords(props.longDescription),
                }}
              />
            </Box>
          )}
        </VStack>
        {props.showButtonTop && renderButtons()}
      </VStack>
      <VStack gap={8} width={'full'}>
        {props.featuredImage && (
          <Box width={'full'}>
            <Image
              asChild
              alt={props.imageAltText}
              src={props.featuredImage}
              style={{ filter: 'drop-shadow(0px 4px 20px rgba(0, 0, 0, 0.25))' }}
            >
              <NextImage {...({} as any)} />
            </Image>
          </Box>
        )}
        {props.children}
        {props.showButtonBottom && renderButtons()}
      </VStack>
    </VStack>
  )
}

export default LPHero
